import { classNames } from '@superfunnel/utils';
import { ClockRotateRight, Plus } from 'iconoir-react';

export interface AgentToolbarProps {
  /** Title displayed on the left side of the toolbar */
  title: string;
  /** Callback when "New chat" button is clicked */
  onNewChat?: () => void;
  /** Callback when history button is clicked (optional - hides button if not provided) */
  onViewHistory?: () => void;
  /** Additional class names for the toolbar container */
  className?: string;
  /** Whether to disable all actions */
  disabled?: boolean;
}

/**
 * A configurable toolbar for agent chat interfaces.
 * Parent component controls visibility and animations.
 */
export function AgentToolbar({
  title,
  onNewChat,
  onViewHistory,
  className,
  disabled = false,
}: AgentToolbarProps) {
  return (
    <div
      className={classNames(
        'flex w-full items-center justify-between rounded-xl border border-white bg-white p-1',
        className,
      )}
    >
      {/* Left side - Title */}
      <div className="flex h-8 shrink-0 grow-0 items-center gap-1.5 rounded-lg py-[7px] pr-2 pl-2.5">
        <p className="shrink-0 grow-0 text-xs font-medium text-black">
          {title}
        </p>
      </div>

      {/* Right side - Actions */}
      <div className="flex shrink-0 grow-0 items-center px-0.5">
        {/* New Chat Button */}
        {onNewChat && (
          <button
            onClick={onNewChat}
            disabled={disabled}
            className="hover:bg-base-50 flex h-8 shrink-0 grow-0 items-center gap-2 rounded-lg py-[7px] pr-3 pl-2.5 disabled:pointer-events-none disabled:opacity-50"
          >
            <Plus
              className="size-[18px] shrink-0 text-[#637083]"
              strokeWidth={1.5}
            />
            <span className="shrink-0 grow-0 text-[13px] font-medium text-[#141c25]">
              New chat
            </span>
          </button>
        )}

        {/* Separator and History Button (only if onViewHistory is provided) */}
        {onViewHistory && (
          <>
            <svg
              width="1"
              height="21"
              viewBox="0 0 1 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0 grow-0"
            >
              <line x1="0.5" y1="0" x2="0.5" y2="21" stroke="#E4E7EC" />
            </svg>
            <button
              onClick={onViewHistory}
              disabled={disabled}
              className="hover:bg-base-50 flex size-8 shrink-0 grow-0 items-center justify-center gap-2 rounded-lg px-2.5 py-[7px] disabled:pointer-events-none disabled:opacity-50"
            >
              <ClockRotateRight
                className="size-4 shrink-0 text-[#637083]"
                strokeWidth={1.2}
              />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
